// Frontend renderer registry + projections for the person event log
// (network.person_event). One place maps event_types to how they display and
// which recruitment-pipeline stage (if any) they represent.
//
// TO ADD A NEW EVENT TYPE (the whole frontend change): add one entry to
// `eventTypeMeta` whose key matches the event_type your source app writes via
// PersonEvent.record(...). If it advances the recruitment pipeline, also give it
// a `stage` and teach `projectPipeline` to read it (one line). Unknown types fall
// back to `genericMeta` so nothing ever crashes. See docs/plans/person-event-log/PLAN.md.

const clientName = (p) => p.client_name || p.client_slug || 'kund'

const reviewLink = (p) =>
  p.client_slug && p.review_slug
    ? { to: `/clients/${p.client_slug}/reviews/${p.review_slug}/edit`, label: 'Visa i granskning' }
    : null

const storyLink = (p) =>
  p.story_slug ? { to: `/kayenta_stories/${p.story_slug}`, label: 'Visa story' } : null

const candidateLink = (p) =>
  p.candidate_uuid ? { to: `/recruitment/${p.candidate_uuid}`, label: 'Visa kandidat' } : null

// ---- Recruitment pipeline ------------------------------------------------
// Fixed, ordered stage vocabulary. `prospekt` is the shared first stage: it is
// seeded either by a Kunder recruitment flag (rekrytering_tagged) or by the
// recruitment app's own `potential` milestone — both fold to it. The rest come
// from the recruitment app's kandidat_milestone events. Terminal outcomes are
// listed separately (they end the pipeline rather than being a step along it).
export const PIPELINE_STAGES = [
  { code: 'prospekt',   label: 'Prospekt' },
  { code: 'kontaktad',  label: 'Kontaktad' },
  { code: 'traffad',    label: 'Träffad' },
  { code: 'intervju_1', label: 'Intervju 1' },
  { code: 'intervju_2', label: 'Intervju 2' },
  { code: 'intervju_3', label: 'Intervju 3' },
  { code: 'anstalld',   label: 'Anställd' },
]
export const PIPELINE_TERMINAL = {
  tackade_nej: 'Tackade nej',
  kallnat:     'Kallnat',
}
const STAGE_INDEX = PIPELINE_STAGES.reduce((m, s, i) => { m[s.code] = i; return m }, {})

// candidate_milestone.milestone_type (recruitment app) → our stage_code.
const MILESTONE_TO_STAGE = {
  potential:   'prospekt',
  contacted:   'kontaktad',
  met:         'traffad',
  interview_1: 'intervju_1',
  interview_2: 'intervju_2',
  interview_3: 'intervju_3',
  hired:       'anstalld',
  declined:    'tackade_nej',
  gone_cold:   'kallnat',
}

// A booked interview's slot travels in the kandidat_milestone payload (personSync writes
// it there), so the feed row can show when the interview is — and a reschedule visibly
// changes the row instead of silently re-recording the same text.
//
// Formatted by slicing the string, deliberately NOT through `new Date(...)`. The slot is a
// `timestamp without time zone` — a wall-clock time — and payloads written before that was
// handled properly carry a trailing `Z` that claims UTC. Either form renders as the time
// that was actually booked here; running it through Date would shift the older ones by the
// viewer's offset (a 15:00 interview read as 17:00 in Sweden).
const interviewSlotText = (p) => {
  const raw = p.interview_start_timestamp
  if (!raw) return ''
  const m = String(raw).match(/^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2})/)
  const when = m ? `${m[1]} ${m[2]}` : String(raw)
  return [when, p.interview_location].filter(Boolean).join(' · ')
}

const stageLabel = (code) => {
  const s = PIPELINE_STAGES.find(x => x.code === code)
  return s ? s.label : (PIPELINE_TERMINAL[code] || code || '')
}

// Pure projection: person events -> the recruitment pipeline's state. Folds the
// Kunder interest flag and the recruitment app's candidate milestones — both feed
// the same pipeline, and the furthest stage reached wins.
export function projectPipeline(events = []) {
  const stageEvents = []      // { stage_code, occurred_at, payload }
  const flags = []            // active rekrytering_tagged, for client provenance

  // Recruitment flag: a review (source_ref) is currently active only if its LATEST
  // tag/remove event is a tag. Decide "latest" by person_event_id (insert order),
  // not occurred_at — occurred_at is the flag's chosen date and can be backdated,
  // so it doesn't reliably reflect the tag→remove sequence.
  const latestFlagByRef = new Map()
  for (const ev of events) {
    if (ev.event_type !== 'rekrytering_tagged' && ev.event_type !== 'rekrytering_removed') continue
    const prev = latestFlagByRef.get(ev.source_ref)
    if (!prev || ev.person_event_id > prev.person_event_id) latestFlagByRef.set(ev.source_ref, ev)
  }
  for (const ev of latestFlagByRef.values()) {
    if (ev.event_type === 'rekrytering_tagged') {
      stageEvents.push({ stage_code: 'prospekt', occurred_at: ev.occurred_at, payload: ev.payload || {} })
      flags.push(ev)
    }
  }

  // Candidate milestones (recruitment app): fold per milestone id (latest event
  // wins, by insert order), honoring kandidat_milestone_removed retractions — so an
  // edited/deleted milestone or a deleted candidate never leaves a phantom stage.
  const latestMsById = new Map()
  for (const ev of events) {
    if (ev.event_type !== 'kandidat_milestone' && ev.event_type !== 'kandidat_milestone_removed') continue
    const msId = ev.payload?.candidate_milestone_id
    if (msId == null) continue
    const prev = latestMsById.get(msId)
    if (!prev || ev.person_event_id > prev.person_event_id) latestMsById.set(msId, ev)
  }
  let candidateUuid = null
  for (const ev of latestMsById.values()) {
    if (ev.event_type !== 'kandidat_milestone') continue
    const stage = MILESTONE_TO_STAGE[ev.payload?.milestone_type]
    if (stage) stageEvents.push({ stage_code: stage, occurred_at: ev.occurred_at, payload: ev.payload || {} })
    if (ev.payload?.candidate_uuid) candidateUuid = ev.payload.candidate_uuid
  }

  if (!stageEvents.length) return null

  // Furthest non-terminal stage reached = current stage; terminal outcome (if any) wins the pill.
  const terminal = stageEvents.find(s => PIPELINE_TERMINAL[s.stage_code])
  const reached = new Set(stageEvents.map(s => s.stage_code).filter(c => c in STAGE_INDEX))
  let currentIdx = -1
  for (const c of reached) currentIdx = Math.max(currentIdx, STAGE_INDEX[c])

  // Client provenance: group the interest flags by client with a count each.
  const byClient = new Map()
  for (const f of flags) {
    const key = f.payload?.client_slug || f.payload?.client_name || '—'
    const cur = byClient.get(key) || { client_slug: f.payload?.client_slug, client_name: clientName(f.payload || {}), count: 0, latest: f }
    cur.count += 1
    byClient.set(key, cur)
  }

  return {
    reached,
    currentIdx,
    currentStage: currentIdx >= 0 ? PIPELINE_STAGES[currentIdx] : null,
    candidateUuid,
    terminal: terminal ? { code: terminal.stage_code, label: PIPELINE_TERMINAL[terminal.stage_code] } : null,
    provenance: [...byClient.values()].sort((a, b) => b.count - a.count),
    // interest-only = only the shared first stage exists (nothing past prospekt),
    // e.g. a Kunder flag before any recruitment milestone advances it.
    interestOnly: currentIdx <= 0 && !terminal,
  }
}

// ---- Networking ("Nätverkande") ------------------------------------------
// Networking events logged on a person from the Nätverk app. Append-only like
// the rest of person_event: an edit re-records `natverkande_noterad` with the
// same source_ref, a delete records `natverkande_removed`. Both the per-person
// section and the cross-person overview page fold with projectNetworking.
export const NETWORKING_TYPE_LABELS = {
  rekrytering: 'Rekrytering',
  salj: 'Sälj',
  annat: 'Annat',
}

// Fold networking events to the current entry per source_ref (globally unique,
// so this serves one person's events or the whole cross-person feed). Latest by
// person_event_id (insert order) wins; a source_ref whose latest is a removal is
// dropped. Returns entries newest-first, each with source_ref + the payload
// fields flattened (and person_name/linkedin when present, i.e. the overview).
export function projectNetworking(events = []) {
  const latestByRef = new Map()
  for (const ev of events) {
    if (ev.event_type !== 'natverkande_noterad' && ev.event_type !== 'natverkande_removed') continue
    if (!ev.source_ref) continue
    const prev = latestByRef.get(ev.source_ref)
    if (!prev || ev.person_event_id > prev.person_event_id) latestByRef.set(ev.source_ref, ev)
  }

  return [...latestByRef.values()]
    .filter(ev => ev.event_type === 'natverkande_noterad')
    .sort((a, b) => b.person_event_id - a.person_event_id)
    .map(ev => {
      const p = ev.payload || {}
      return {
        source_ref: ev.source_ref,
        person_uuid: ev.person_uuid,
        person_name: ev.person_name || null,
        person_linkedin: ev.person_linkedin || null,
        occurred_at: ev.occurred_at,
        author: p.author_name || ev.modified_by_user_username || null,
        type: p.type || null,
        role: p.role || null,
        company_uuid: p.company_uuid || null,
        company_name: p.company_name || null,
        purpose: p.purpose || null,
        result: p.result || null,
        next_step: p.next_step || null,
      }
    })
}

// ---- Per-event display registry (feed rows) ------------------------------
export const eventTypeMeta = {
  rekrytering_tagged: {
    icon: '🎯', label: 'Rekrytering', source: 'rekrytering', isRetraction: false,
    render: (p) => ({
      title: `Prospekt · ${clientName(p)}`,
      subtitle: [p.role, p.period].filter(Boolean).join(' · '),
      link: reviewLink(p),
    }),
  },
  rekrytering_removed: {
    icon: '✖️', label: 'Rekrytering', source: 'rekrytering', isRetraction: true,
    render: (p) => ({
      title: `Inte längre aktuell · ${clientName(p)}`,
      subtitle: [p.role, p.period].filter(Boolean).join(' · '),
      link: reviewLink(p),
    }),
  },
  // Recruitment pipeline milestones (recruitment app). The stage label is derived
  // from the milestone_type; the source dot is the blue `kandidat` identity.
  kandidat_milestone: {
    icon: '👤', label: 'Kandidat', source: 'kandidat', isRetraction: false,
    render: (p) => ({
      title: stageLabel(MILESTONE_TO_STAGE[p.milestone_type]),
      subtitle: [interviewSlotText(p), p.note].filter(Boolean).join(' · '),
      link: candidateLink(p),
    }),
  },
  kandidat_milestone_removed: {
    icon: '✖️', label: 'Kandidat', source: 'kandidat', isRetraction: true,
    render: (p) => ({
      title: `Återtaget · ${stageLabel(MILESTONE_TO_STAGE[p.milestone_type])}`,
      subtitle: p.note || '',
      link: candidateLink(p),
    }),
  },
  // Networking ("Nätverkande") — logged on a person, mirrored to a Teams channel.
  natverkande_noterad: {
    icon: '🤝', label: 'Nätverkande', source: 'natverkande', isRetraction: false,
    render: (p) => ({
      title: ['Nätverkande', NETWORKING_TYPE_LABELS[p.type] || p.type, p.company_name].filter(Boolean).join(' · '),
      subtitle: [p.purpose, p.next_step].filter(Boolean).join(' · '),
      link: null,
    }),
  },
  natverkande_removed: {
    icon: '✖️', label: 'Nätverkande', source: 'natverkande', isRetraction: true,
    render: (p) => ({
      title: ['Borttaget nätverkande', p.company_name].filter(Boolean).join(' · '),
      subtitle: '',
      link: null,
    }),
  },
  // Stories as events (PR6 / person-event-log Phase 4) — the Stories section
  // in Nuläge stays a live join (Involvement.getStoryInvolvements); these
  // entries only light up the Alla händelser raw feed.
  talare_tagged: {
    icon: '🎤', label: 'Stories', source: 'story', isRetraction: false,
    render: (p) => ({
      title: `Talare · ${p.topic || 'story'}`,
      subtitle: p.role_title || '',
      link: storyLink(p),
    }),
  },
  talare_removed: {
    icon: '✖️', label: 'Stories', source: 'story', isRetraction: true,
    render: (p) => ({
      title: `Inte längre talare · ${p.topic || 'story'}`,
      subtitle: p.role_title || '',
      link: storyLink(p),
    }),
  },
  deltagare_tagged: {
    icon: '🎤', label: 'Stories', source: 'story', isRetraction: false,
    render: (p) => ({
      title: `Deltagare · ${p.topic || 'story'}`,
      subtitle: '',
      link: storyLink(p),
    }),
  },
  deltagare_removed: {
    icon: '✖️', label: 'Stories', source: 'story', isRetraction: true,
    render: (p) => ({
      title: `Inte längre deltagare · ${p.topic || 'story'}`,
      subtitle: '',
      link: storyLink(p),
    }),
  },
}

export const genericMeta = {
  icon: '•', label: 'Händelse', source: 'other', isRetraction: false,
  render: () => ({ title: 'Händelse', subtitle: '', link: null }),
}

export const metaFor = (eventType) => eventTypeMeta[eventType] || genericMeta

// Source identity for feed dots + section headers — mirrors the app's pill palette.
export const eventSourceMeta = {
  rekrytering: { label: 'Rekrytering',    color: '#6B4FA0', icon: '🎯' },
  kandidat:    { label: 'Kandidat',       color: '#3D6AA3', icon: '👤' },
  natverkande: { label: 'Nätverkande',    color: '#2E7D7B', icon: '🤝' },
  event:       { label: 'Event & mingel', color: '#A04A8F', icon: '🎟️' },
  story:       { label: 'Stories',        color: '#8A6A15', icon: '🎤' },
  kundcase:    { label: 'Kundcase',       color: '#1F9D78', icon: '🏢' },
  other:       { label: 'Övrigt',         color: '#857c77', icon: '•' },
}
